import React, {useContext} from "react";
import {useTranslation} from "react-i18next";
import {GlobalDataContext} from "../../../contexts/GlobalDataContext";
import {RegionSelectedContext} from "../../../contexts/RegionSelectedContext";
import Barchart, {BarData} from "../../Charts/Barchart";
import EuropeRecord from "../../../models/EuropeRecord";
import Branch from "../../../interfaces/Branch";
import {HorizontalSeparator} from "../../separators/Separators";
import styled from "styled-components";
import TwoColumnsLayout from "../../TwoColumnsLayout/TwoColumnsLayout";

const MarketAnalysisContentComponent: React.FC<{openDetails: () => void}> = ({openDetails}) => {
    const { t } = useTranslation();

    const { europeRecords } = useContext(GlobalDataContext);
    const { yearSelected } = useContext(RegionSelectedContext);

    if (Object.keys(europeRecords).length === 0) {
        return <div />
    }

    const records: BarData[] = europeRecords
        .sort((r1: EuropeRecord, r2: EuropeRecord) => r2.premiums_current_year - r1.premiums_current_year)
        .filter((record: EuropeRecord) => record.year === yearSelected.year && record.niiff === yearSelected.niiff)
        .filter((record: EuropeRecord) => record.branch === Branch.TOTAL)
        .map((record: EuropeRecord) => {
            return {
                group: record.company_group,
                bars: [record.premiums_last_10_years, record.increment_last_9_years, record.increment_last_year],
                point: record.premiums_current_year
            };
        });

    const description = (
      <div>
        <p className={'marketAnalysisText'}>
          { t(`marketAnalysis.${yearSelected.niiff ? "niif." : ""}paragraph`) }
        </p>
        <div className='see-more-details' onClick={openDetails}>
          { t('marketAnalysis.seeMoreDetails') }
        </div>
      </div>
    );

    return (
        <>
          <TwoColumnsLayout
            leftColumnChildren={
              <>
                <h3 className={'marginTop50'}>
                  { t(`marketAnalysis.${yearSelected.niiff ? "niif." : ""}subtitle`) }
                </h3>
                <HorizontalSeparator width={50}/>
                { description }
              </>
            }
            rightColumnChildren={
              <div className='chart-container'>
                <h3 style={{ marginBottom: 10 }}>{ t('marketAnalysis.chartTitle', { 'year': yearSelected.year }) }</h3>
                <LegendComponent />
                <div>
                  <Barchart
                      bars={ records }
                      isDark={ false }
                      barColors={ ['#FFFFFF', '#464646', '#FAA3A3'] }
                  />
                </div>
              </div>
            }
          />
        </>
    )
};

export default MarketAnalysisContentComponent;


const LegendComponent: React.FC = () => {
    const { t } = useTranslation();

    const { yearSelected } = useContext(RegionSelectedContext);

    return(
        <LineMeaningContainer>
            <div className={'line-row'}>
                <ColorBox color={'#FFFFFF'}/>
                <div className={'legend-title'}>
                  { t(`marketAnalysis.${yearSelected.niiff ? "niif." : ""}barChart.legend.last10Years`, { year: yearSelected.year - 10 }) }
                </div>
            </div>

            <div className={'line-row'}>
                <ColorBox color={'#464646'}/>
                <div className={'legend-title'}>
                  { t(`marketAnalysis.${yearSelected.niiff ? "niif." : ""}barChart.legend.incrementLast9Years`, { yearStart: yearSelected.year - 10, yearEnd: yearSelected.year - 1 }) }
                </div>
            </div>
            <div className={'line-row'}>
                <ColorBox color={'#FAA3A3'}/>
                <div className={'legend-title'}>
                  { t(`marketAnalysis.${yearSelected.niiff ? "niif." : ""}barChart.legend.incrementLast9Years`, { yearStart: yearSelected.year - 1, yearEnd: yearSelected.year }) }
                </div>
            </div>
            <div className={'line-row'}>
                <BiColorBox color={'#CC0000'}/>
                <div className={'legend-title'}>
                  { t(`marketAnalysis.${yearSelected.niiff ? "niif." : ""}barChart.legend.last10Years`, { year: yearSelected.year }) }
                </div>
            </div>
        </LineMeaningContainer>
    )
};

const ColorBox = styled.div`
    height: 0.7rem;
    min-height: 0.7rem;
    width: 0.7rem;
    min-width: 0.7rem;
    background-color: ${(props: {color: string}) => props.color };
    border-radius: 2px;
`;

const BiColorBox = styled.div`
    border-width: 2px;
    border: solid ${(props: {color: string}) => props.color };
    height: 0.7rem;
    min-height: 0.7rem;
    width: 0.7rem;
    min-width: 0.7rem;
    background-color: white;
    border-radius: 0.7rem;
`;

const LineMeaningContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex: 0 0 auto;
    font-family: MapfreText-Light;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    flex-wrap: wrap;

    .line-row {
        display: flex;
        flex-direction: row;
        flex: 1 0 0;
        align-items: center;
        margin-left: 5px;
        margin-right: 5px;
    }

    .legend-title {
        margin-left: 10px;
    }

    @media (max-width: 480px) {
      font-size: 0.7rem;
    }
`;
